import {
  Component,
  OnDestroy,
  Input,
} from "@angular/core";

import { Observable, Subscription } from "rxjs";
import { select, Store } from "@ngrx/store";

import {
  getFccDeviceSubtypesList,
  getSiteFccList,
  getSiteFuelPointList,
  getSitePosList,
  getSitePriceSignList,
} from "src/app/store/actions/sites/sites.actions";
import { getTankGauges } from "src/app/store/actions/tank-gauges/tank-gauges.actions";
import {
  AppState,
  siteFccsLoadedState,
  siteFccDeviceSubTypesLoadedState,
  sitePosLoadedState,
  siteFuelPointLoadedState,
  sitePriceSignsLoadedState,
  tankGaugeListState,
} from "src/app/store/reducers";

import {
  FccType,
  SiteDeviceFccDTO,
  SiteFccDTO,
} from "src/app/models/Sites/siteFccDTO";
import { SitePosDTO } from "src/app/models/Sites/sitePosDTO";
import { SiteFuelPointDTO } from "src/app/models/Sites/siteFuelPointDTO";
import { TankLevelGaugesDTO } from "src/app/models/tank-gauges/tank-gaugesDTO";
import { SitePriceSignDTO } from "src/app/models/Sites/sitePriceSignDTO";

import { FccFormDialogComponent } from "./dialog/fcc-form-dialog.component";
import { MatDialog } from "@angular/material/dialog";

@Component({
  selector: "site-fcc-details",
  templateUrl: "./fccs-details.component.html",
  styleUrls: ["./fccs-details.component.scss"],
})
export class FccDetailsComponent implements OnDestroy {
  _siteCode: string;

  @Input() set siteCode(value: string) {
    this._siteCode = value;
    if (this._siteCode) {
      this.loadFccs(this._siteCode);
      this.loadFccDeviceTypes();
      this.loadSiteFuelPoints(this._siteCode);
      this.loadSitePos(this._siteCode);
      this.loadSitePriceSigns(this._siteCode);
      this.loadSiteTankLevelGauges(this._siteCode);
    }
  }

  @Input() permissionResource: string;

  //Subscriptions
  fccSub: Subscription;
  fccTypeSub: Subscription;
  posSub: Subscription;
  fuelPointSub: Subscription;
  priceSignSub: Subscription;
  tankLevelGaugeSub: Subscription;

  //FCC list
  fccList$: Observable<SiteDeviceFccDTO[]>;
  fccList: SiteDeviceFccDTO[];

  posList$: Observable<SitePosDTO[]>;
  posList: SitePosDTO[];

  fuelPointList$: Observable<SiteFuelPointDTO[]>;
  fuelPointList: SiteFuelPointDTO[];

  tlgList$: Observable<TankLevelGaugesDTO[]>;
  tlgList: TankLevelGaugesDTO[];

  priceSignList$: Observable<SitePriceSignDTO[]>;
  priceSignList: SitePriceSignDTO[];

  fccTypeList$: Observable<FccType[]>;
  fccTypeList: FccType[];

  currentFcc: SiteDeviceFccDTO;
  isFccSelected = false;

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog
  ) {
    this.fccList$ = this.store.pipe(select(siteFccsLoadedState));
    this.fccTypeList$ = this.store.pipe(select(siteFccDeviceSubTypesLoadedState));
    this.posList$ = this.store.pipe(select(sitePosLoadedState));
    this.fuelPointList$ = this.store.pipe(select(siteFuelPointLoadedState));
    this.priceSignList$ = this.store.pipe(select(sitePriceSignsLoadedState));
    this.tlgList$ = this.store.pipe(select(tankGaugeListState));
  }

  loadFccs(siteCode: string) {
    this.fccSub = this.fccList$.subscribe((fccs: SiteFccDTO[]) => {
      this.fccList = fccs;
      if (fccs && fccs.length && this.currentFcc) {
        const { code: currentFccCode } = this.currentFcc;
        this.currentFcc = fccs.find(({ code }) => code === currentFccCode);
      }
      this.resetFcc();
    });
    this.store.dispatch(
      getSiteFccList({
        code: siteCode,
        pageReq: { realSize: true, offset: 0, limit: 999 },
      })
    );
  }

  loadFccDeviceTypes() {
    this.fccTypeSub = this.fccTypeList$.subscribe((typeList) => {
      this.fccTypeList = typeList;
    });
    this.store.dispatch(getFccDeviceSubtypesList(null));
  }

  loadSitePos(siteCode: string) {
    this.posSub = this.posList$.subscribe((pos) => {
      this.posList = pos;
    });
    this.store.dispatch(
      getSitePosList({
        code: siteCode,
        pageReq: { realSize: true, offset: 0, limit: 999 },
      })
    );
  }

  loadSiteFuelPoints(siteCode: string) {
    this.fuelPointSub = this.fuelPointList$.subscribe((fuelPoints) => {
      this.fuelPointList = fuelPoints;
    });
    this.store.dispatch(
      getSiteFuelPointList({
        code: siteCode,
        pageReq: { realSize: true, offset: 0, limit: 999 },
      })
    );
  }

  loadSitePriceSigns(siteCode: string) {
    this.priceSignSub = this.priceSignList$.subscribe((priceSigns) => {
      this.priceSignList = priceSigns;
    });
    this.store.dispatch(
      getSitePriceSignList({
        code: siteCode,
        pageReq: { realSize: true, offset: 0, limit: 999 },
      })
    );
  }

  loadSiteTankLevelGauges(siteCode: string) {
    this.tankLevelGaugeSub = this.tlgList$.subscribe((tlgs) => {
      this.tlgList = tlgs;
    });
    this.store.dispatch(
      getTankGauges({
        code: siteCode,
        pageReq: { realSize: true, offset: 0, limit: 999 },
      })
    );
  }

  getFccTypeDescription(fcc: SiteDeviceFccDTO) {
    if (fcc && this.fccTypeList && this.fccTypeList.length) {
      return this.fccTypeList.find(({ code }) => code === fcc.protocolTypeCode)
        .description;
    }
  }

  resetFcc() {
    this.dialog.closeAll();
    this.isFccSelected = false;
    this.currentFcc = null;
  }

  selectFcc(fcc: SiteDeviceFccDTO) {
    this.isFccSelected = true;
    this.currentFcc = fcc;

    this.openFccFormDialog(false);
  }

  create() {
    this.isFccSelected = false;
    this.currentFcc = null;

    this.openFccFormDialog(true);
  }

  openFccFormDialog(isCreate: boolean) {
    this.dialog.open(FccFormDialogComponent, {
      autoFocus: false,
      data: {
        isCreate: isCreate,
        siteCode: this._siteCode,
        currentFcc: this.currentFcc,
        fccTypeList: this.fccTypeList,
        priceSignList: this.priceSignList,
        fuelPointList: this.fuelPointList,
        tlgList: this.tlgList,
        posList: this.posList,
      }
    });
  }

  ngOnDestroy(): void {
    if (this.fccSub) {
      this.fccSub.unsubscribe();
    }
    if (this.fccTypeSub) {
      this.fccTypeSub.unsubscribe();
    }
    if (this.posSub) {
      this.posSub.unsubscribe();
    }
    if (this.fuelPointSub) {
      this.fuelPointSub.unsubscribe();
    }
    if (this.priceSignSub) {
      this.priceSignSub.unsubscribe();
    }
    if (this.tankLevelGaugeSub) {
      this.tankLevelGaugeSub.unsubscribe();
    }
  }
}
